import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { examActions, questionActions, questionTypeActions, questionTopicActions } from '../../redux/actions';
import { AppDispatch, RootState } from '../../store';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormSection,
  SectionTitle,
  FormRow,
  FormGroup,
  Label,
  Required,
  Input,
  TextArea,
  Select,
  TabContainer,
  Tab,
  QuestionSelectionCard,
  InfoText,
  ModalFooter,
  Button,
  QuestionListContainer,
  QuestionItem,
  QuestionItemHeader,
  QuestionItemTitle,
  QuestionItemBadge,
  QuestionItemContent,
  SelectedCount,
} from './ExamFormModal.styled';

interface ExamFormModalProps {
  exam: any;
  onClose: () => void;
}

type QuestionSelectionMethod = 'later' | 'manual' | 'filter' | 'auto';

const ExamFormModal: React.FC<ExamFormModalProps> = ({ exam, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const questionsList = useSelector((state: RootState) => (state.questions as any)?.questionsList || []);
  const questionTypes = useSelector((state: RootState) => (state.questionTypes as any)?.questionTypeList || []);
  const questionTopics = useSelector((state: RootState) => (state.questionTopics as any)?.questionTopicList || []);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 60,
    applicable_classes: '',
    total_marks: 10,
    difficulty_level: '',
    status: 'draft' as 'draft' | 'published',
  });

  const [questionMethod, setQuestionMethod] = useState<QuestionSelectionMethod>('later');
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [filterCriteria, setFilterCriteria] = useState({
    question_type_ids: [] as number[],
    difficulty: '',
    topic_ids: [] as number[],
    count: 10,
  });
  const [submitting, setSubmitting] = useState(false);
  const [examQuestions, setExamQuestions] = useState<any[]>([]);
  const [questionsToRemove, setQuestionsToRemove] = useState<number[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Load exam data if editing
  useEffect(() => {
    if (exam) {
      setFormData({
        title: exam.title || '',
        description: exam.description || '',
        duration: exam.duration || 60,
        applicable_classes: exam.applicable_classes || '',
        total_marks: exam.total_marks || 10,
        difficulty_level: exam.difficulty_level || '',
        status: exam.status || 'draft',
      });

      // Fetch full exam details including questions
      const fetchExamQuestions = async () => {
        setLoadingQuestions(true);
        try {
          const fullExam: any = await dispatch(examActions.getById(exam.id));
          if (fullExam?.data?.exam_questions) {
            setExamQuestions(fullExam.data.exam_questions);
          }
        } catch (error) {
          console.error('Error loading exam questions:', error);
        } finally {
          setLoadingQuestions(false);
        }
      };

      fetchExamQuestions();
    }
  }, [exam, dispatch]);

  // Fetch questions for manual selection
  useEffect(() => {
    if (questionMethod === 'manual') {
      dispatch(questionActions.getAllPaginated({ page: 1, limit: 100 }));
    }
  }, [questionMethod, dispatch]);

  // Fetch question types and topics for filter/auto selection
  useEffect(() => {
    if (questionMethod === 'filter' || questionMethod === 'auto') {
      dispatch(questionTypeActions.getAll());
      dispatch(questionTopicActions.getAll());
    }
  }, [questionMethod, dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' || name === 'total_marks' ? parseInt(value) || 0 : value
    }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilterCriteria(prev => ({
      ...prev,
      [name]: name === 'count' ? parseInt(value) || 0 : value
    }));
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>, field: 'question_type_ids' | 'topic_ids') => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => parseInt(option.value));
    setFilterCriteria(prev => ({
      ...prev,
      [field]: selectedOptions
    }));
  };

  const toggleQuestionSelection = (questionId: number) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleMarkQuestionForRemoval = (questionId: number) => {
    setQuestionsToRemove(prev => [...prev, questionId]);
    // Also remove from display list
    setExamQuestions(prev => prev.filter(eq => eq.question_id !== questionId));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Vui lòng nhập tên đề thi!');
      return;
    }

    if (!formData.duration || formData.duration <= 0) {
      alert('Vui lòng nhập thời gian làm bài hợp lệ!');
      return;
    }

    setSubmitting(true);

    try {
      let examData = {
        ...formData,
        id: exam?.id,
      };

      // Create or update exam
      let createdExam;
      if (exam?.id) {
        createdExam = await dispatch(examActions.updateExam(examData));
        
        // Remove marked questions from exam
        if (questionsToRemove.length > 0) {
          for (const questionId of questionsToRemove) {
            await dispatch(examActions.removeQuestion(exam.id, questionId));
          }
        }
      } else {
        createdExam = await dispatch(examActions.createExam(examData));
      }

      // Add questions if method is selected
      if (questionMethod !== 'later' && createdExam?.data) {
        const examId = createdExam.data.id;

        if (questionMethod === 'manual' && selectedQuestions.length > 0) {
          await dispatch(examActions.addQuestions(examId, {
            method: 'manual',
            question_ids: selectedQuestions,
          }));
        } else if (questionMethod === 'filter' || questionMethod === 'auto') {
          const filters: any = {};
          if (filterCriteria.question_type_ids.length > 0) {
            filters.question_type_ids = filterCriteria.question_type_ids;
          }
          if (filterCriteria.difficulty) {
            filters.difficulty = filterCriteria.difficulty;
          }
          if (filterCriteria.topic_ids.length > 0) {
            filters.topic_ids = filterCriteria.topic_ids;
          }

          await dispatch(examActions.addQuestions(examId, {
            method: questionMethod,
            filters,
            count: filterCriteria.count,
          }));
        }
      }

      onClose();
    } catch (error) {
      console.error('Error creating/updating exam:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{exam ? '✏️ Chỉnh Sửa Đề Thi' : '📝 Tạo Đề Thi Mới'}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Basic Information */}
          <FormSection>
            <SectionTitle>📋 Thông Tin Cơ Bản</SectionTitle>
            
            <FormRow columns={1}>
              <FormGroup>
                <Label>
                  Tên đề thi <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="VD: Kiểm tra giữa kỳ 1 - Lớp 10"
                />
              </FormGroup>
            </FormRow>

            <FormRow columns={1}>
              <FormGroup>
                <Label>Mô tả</Label>
                <TextArea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Mô tả nội dung, yêu cầu của đề thi..."
                  rows={3}
                />
              </FormGroup>
            </FormRow>

            <FormRow columns={3}>
              <FormGroup>
                <Label>
                  Thời gian (phút) <Required>*</Required>
                </Label>
                <Input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="60"
                />
              </FormGroup>

              <FormGroup>
                <Label>Tổng điểm</Label>
                <Input
                  type="number"
                  name="total_marks"
                  value={formData.total_marks}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="100"
                />
              </FormGroup>

              <FormGroup>
                <Label>Độ khó</Label>
                <Select
                  name="difficulty_level"
                  value={formData.difficulty_level}
                  onChange={handleInputChange}
                >
                  <option value="">Chưa xác định</option>
                  <option value="easy">Dễ</option>
                  <option value="medium">Trung bình</option>
                  <option value="hard">Khó</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow columns={2}>
              <FormGroup>
                <Label>Lớp áp dụng</Label>
                <Input
                  type="text"
                  name="applicable_classes"
                  value={formData.applicable_classes}
                  onChange={handleInputChange}
                  placeholder="VD: Lớp 10A, 10B, 11C"
                />
              </FormGroup>

              <FormGroup>
                <Label>Trạng thái</Label>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="draft">Bản nháp</option>
                  <option value="published">Đã xuất bản</option>
                </Select>
              </FormGroup>
            </FormRow>
          </FormSection>

          {/* Question Selection - Only show when creating new exam */}
          {!exam && (
            <FormSection>
              <SectionTitle>❓ Thêm Câu Hỏi</SectionTitle>
              
              <TabContainer>
                <Tab 
                  active={questionMethod === 'later'} 
                  onClick={() => setQuestionMethod('later')}
                >
                  Thêm sau
                </Tab>
                <Tab 
                  active={questionMethod === 'manual'} 
                  onClick={() => setQuestionMethod('manual')}
                >
                  Chọn thủ công
                </Tab>
                <Tab 
                  active={questionMethod === 'filter'} 
                  onClick={() => setQuestionMethod('filter')}
                >
                  Lọc điều kiện
                </Tab>
                <Tab 
                  active={questionMethod === 'auto'} 
                  onClick={() => setQuestionMethod('auto')}
                >
                  Tự động ngẫu nhiên
                </Tab>
              </TabContainer>

              {questionMethod === 'later' && (
                <QuestionSelectionCard>
                  <InfoText>
                    ℹ️ Bạn có thể tạo đề thi trống và thêm câu hỏi sau. 
                    Sau khi tạo xong, click vào đề thi để thêm câu hỏi.
                  </InfoText>
                </QuestionSelectionCard>
              )}

              {questionMethod === 'manual' && (
                <QuestionSelectionCard>
                  <InfoText style={{ marginBottom: '12px' }}>
                    🎯 Chọn các câu hỏi bạn muốn thêm vào đề thi:
                  </InfoText>
                  <QuestionListContainer>
                    {questionsList.length === 0 ? (
                      <InfoText style={{ textAlign: 'center', padding: '20px' }}>
                        Chưa có câu hỏi nào. Hãy tạo câu hỏi trước!
                      </InfoText>
                    ) : (
                      questionsList.map((question: any) => (
                        <QuestionItem
                          key={question.id}
                          selected={selectedQuestions.includes(question.id)}
                          onClick={() => toggleQuestionSelection(question.id)}
                        >
                          <QuestionItemHeader>
                            <QuestionItemTitle>Câu {question.id}</QuestionItemTitle>
                            <QuestionItemBadge type={question.difficulty}>
                              {question.difficulty || 'N/A'}
                            </QuestionItemBadge>
                          </QuestionItemHeader>
                          <QuestionItemContent>
                            {question.content?.substring(0, 100) || 'No content'}...
                          </QuestionItemContent>
                        </QuestionItem>
                      ))
                    )}
                  </QuestionListContainer>
                  {selectedQuestions.length > 0 && (
                    <SelectedCount>
                      ✓ Đã chọn: {selectedQuestions.length} câu hỏi
                    </SelectedCount>
                  )}
                </QuestionSelectionCard>
              )}

              {(questionMethod === 'filter' || questionMethod === 'auto') && (
                <QuestionSelectionCard>
                  <InfoText style={{ marginBottom: '16px' }}>
                    {questionMethod === 'filter' 
                      ? '🔍 Lọc câu hỏi theo điều kiện sau:' 
                      : '🎲 Chọn ngẫu nhiên câu hỏi theo điều kiện:'}
                  </InfoText>
                  
                  <FormRow columns={2}>
                    <FormGroup>
                      <Label>Loại câu hỏi (giữ Ctrl/Cmd để chọn nhiều)</Label>
                      <Select
                        multiple
                        value={filterCriteria.question_type_ids.map(String)}
                        onChange={(e) => handleMultiSelectChange(e, 'question_type_ids')}
                        style={{ minHeight: '120px' }}
                      >
                        {questionTypes.length === 0 ? (
                          <option disabled>Đang tải...</option>
                        ) : (
                          questionTypes.map((type: any) => (
                            <option key={type.id} value={type.id}>
                              {type.vn_title || type.type_name || type.eng_title}
                            </option>
                          ))
                        )}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Chủ đề (giữ Ctrl/Cmd để chọn nhiều)</Label>
                      <Select
                        multiple
                        value={filterCriteria.topic_ids.map(String)}
                        onChange={(e) => handleMultiSelectChange(e, 'topic_ids')}
                        style={{ minHeight: '120px' }}
                      >
                        {questionTopics.length === 0 ? (
                          <option disabled>Đang tải...</option>
                        ) : (
                          questionTopics.map((topic: any) => (
                            <option key={topic.id} value={topic.id}>
                              {topic.vn_name || topic.eng_name}
                            </option>
                          ))
                        )}
                      </Select>
                    </FormGroup>
                  </FormRow>

                  <FormRow columns={2}>
                    <FormGroup>
                      <Label>Độ khó</Label>
                      <Select
                        name="difficulty"
                        value={filterCriteria.difficulty}
                        onChange={handleFilterChange}
                      >
                        <option value="">Tất cả</option>
                        <option value="easy">Dễ</option>
                        <option value="medium">Trung bình</option>
                        <option value="hard">Khó</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Số lượng câu hỏi</Label>
                      <Input
                        type="number"
                        name="count"
                        value={filterCriteria.count}
                        onChange={handleFilterChange}
                        min="1"
                        max="100"
                        placeholder="10"
                      />
                    </FormGroup>
                  </FormRow>

                  <InfoText style={{ marginTop: '12px', fontStyle: 'italic' }}>
                    💡 {questionMethod === 'auto' 
                      ? 'Hệ thống sẽ chọn ngẫu nhiên các câu hỏi phù hợp với điều kiện.' 
                      : 'Hệ thống sẽ lấy các câu hỏi mới nhất phù hợp với điều kiện.'}
                  </InfoText>
                </QuestionSelectionCard>
              )}
            </FormSection>
          )}

          {/* Show existing questions when editing exam */}
          {exam && examQuestions.length > 0 && (
            <FormSection>
              <SectionTitle>📝 Câu hỏi trong đề thi ({examQuestions.length} câu)</SectionTitle>
              {loadingQuestions ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Đang tải câu hỏi...
                </div>
              ) : (
                <QuestionListContainer style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {examQuestions.map((examQuestion: any, index: number) => {
                    const question = examQuestion.question;
                    const questionType = question?.questionType;
                    
                    return (
                      <QuestionItem key={examQuestion.question_id || index}>
                        <QuestionItemHeader>
                          <QuestionItemTitle>
                            <strong>Câu {index + 1}</strong> - {questionType?.vn_title || 'N/A'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                              (ID: {question?.id})
                            </span>
                          </QuestionItemTitle>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <QuestionItemBadge>
                              {examQuestion.score || 1} điểm
                            </QuestionItemBadge>
                            <button
                              type="button"
                              onClick={() => handleMarkQuestionForRemoval(examQuestion.question_id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#c82333'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
                            >
                              🗑️ Xóa
                            </button>
                          </div>
                        </QuestionItemHeader>
                        <QuestionItemContent style={{ fontSize: '13px', color: '#666' }}>
                          {typeof question?.content === 'string' 
                            ? question.content.substring(0, 150) 
                            : JSON.stringify(question?.content || {}).substring(0, 150)
                          }...
                        </QuestionItemContent>
                      </QuestionItem>
                    );
                  })}
                </QuestionListContainer>
              )}
            </FormSection>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Hủy
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '⏳ Đang xử lý...' : (exam ? '💾 Cập nhật' : '✅ Tạo đề thi')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ExamFormModal;
